import React, { useEffect, useState } from "react";
import { atom, useRecoilState } from "recoil";
import dayjs from "dayjs";
import styled from "styled-components";
import LineBox from "../../components/DefaultStyles/LineBox";
import PageTitle from "../../components/DefaultStyles/PageTitle";
import {
  ChatSection,
  ChatSectionTitle,
} from "../../components/pages/Party/ChatInfo/ChatSearch/ChatSearchResult";
import MatchingLog from "../../components/pages/UserLog/MatchingLog";
import Pagination from "../../components/Table/Pagination";
import Table, { TableBody, TableBodyItem } from "../../components/Table/Table";
import { useApi } from "../../hooks/useApi";
import Loader from "../../components/DefaultStyles/Loader";
import ExcelDownload from "../../components/DefaultStyles/ExcelDownload";
import UserLogFilter from "../../components/pages/UserLog/UserLogFilter";
import { useNavigate } from "react-router-dom";
import SpaceBetween from "../../components/DefaultStyles/SpaceBetween";
import { TotalCount } from "../Users/UserInfo";
import { getThousandComma } from "../../Api/ApiCommon";

interface IUserLog {
  uid: string;
  nickname: string;
  name: string;
  logdate: string;
  logdata: {
    prenick?: string;
    ip?: string;
    partyid?: string;
    phone?: string;
    card?: string;
    acc?: string;
  };
}

export const userLogPageState = atom<number>({
  key: "userLogPageState",
  default: 0,
});

export default function UserLog() {
  const navigate = useNavigate();
  const [savedPage, setSavedPage] = useRecoilState<number>(userLogPageState);

  const [page, setPage] = useState(savedPage);

  useEffect(() => {
    setSavedPage(0);
  }, []);
  const [filter, setFilter] = useState({
    type: "",
    typeLabel: "",
    searchValue: "",
    searchType: "",
    startDate: "",
    endDate: "",
    isDateClick: false,
  });

  const { response, loading } = useApi({
    method: "get",
    url:
      filter?.type && filter?.type !== "106"
        ? `log?page=${page}&size=10` +
          (filter?.type !== "" ? `&type=${filter?.type}` : ``) +
          (filter?.searchValue !== ""
            ? `&${filter?.searchType}=${filter?.searchValue}`
            : ``) +
          (filter?.endDate !== ""
            ? `&startdate=${filter?.startDate}&enddate=${filter?.endDate}`
            : ``)
        : undefined,
  });

  const tableHeaderString = (typeValue: string) => {
    switch (typeValue) {
      case "100":
        return "IP";
      case "102":
        return "전화번호";
      case "103":
        return "카드 정보";
      case "104":
        return "계좌번호";
      case "105":
        return "변경 전 닉네임";

      default:
        return "";
    }
  };

  const tableHead =
    filter?.type === "101"
      ? ["PID", "UID", "로그 시간"]
      : ["UID", tableHeaderString(filter?.type!), "로그 시간"];

  return (
    <>
      <PageTitle title="유저 로그" />

      <UserLogFilter filter={filter} setFilter={setFilter} setPage={setPage} />

      {/* 매칭 로그 제외 타입들 */}
      {filter?.type !== "" && filter?.type !== "106" && response?.data && (
        <ChatSection>
          <ChatSectionTitle>{filter?.typeLabel}</ChatSectionTitle>

          <LineBox>
            <Table tableHeadData={tableHead} tableMinWidth={680}>
              <>
                {response?.data?.items?.map((d: IUserLog, index: number) => {
                  return (
                    <TableBody key={index}>
                      <TableBodyItem
                        clickable={true}
                        onClick={() => {
                          setSavedPage(page);
                          if (filter?.type === "101") {
                            navigate(`/party/info/${d?.logdata?.partyid}`);
                          } else {
                            navigate(`/users/info/${d?.uid}`);
                          }
                        }}
                      >
                        {filter?.type === "101"
                          ? d?.logdata?.partyid
                          : `${d?.uid}_${d?.name}_${d?.nickname}`}
                      </TableBodyItem>
                      <TableBodyItem>
                        {filter?.type === "100"
                          ? d?.logdata?.ip
                          : filter?.type === "101"
                          ? `${d?.uid}_${d?.name}_${d?.nickname}`
                          : filter?.type === "102"
                          ? `${d?.logdata?.phone}****`
                          : filter?.type === "103"
                          ? d?.logdata?.card
                          : filter?.type === "104"
                          ? d?.logdata?.acc
                          : filter?.type === "105"
                          ? d?.logdata?.prenick
                          : ``}
                      </TableBodyItem>
                      <TableBodyItem>
                        {dayjs(d?.logdate)?.format("YYYY-MM-DD HH:mm")}
                      </TableBodyItem>
                    </TableBody>
                  );
                })}
              </>
            </Table>

            <SpaceBetween>
              <Pagination
                totalPage={
                  response?.status === 200 ? response.data.totalPage : 10
                }
                currentPage={page}
                pageClickEvent={(index) => setPage(index)}
              />

              <TotalCount>
                총 {getThousandComma(+response?.data?.totalCount)}개
              </TotalCount>
            </SpaceBetween>
          </LineBox>

          <ExcelDownload
            disabled={response?.data?.items?.length < 1}
            tableHead={tableHead.concat(["-"])}
            pushedData={(d: any) => {
              return [
                filter?.type === "101"
                  ? d?.logdata?.partyid
                  : `${d?.uid}_${d?.name}_${d?.nickname}`,
                filter?.type === "100"
                  ? d?.logdata?.ip
                  : filter?.type === "101"
                  ? `${d?.uid}_${d?.name}_${d?.nickname}`
                  : filter?.type === "102"
                  ? `${d?.logdata?.phone}****`
                  : filter?.type === "103"
                  ? d?.logdata?.card
                  : filter?.type === "104"
                  ? d?.logdata?.acc
                  : filter?.type === "105"
                  ? d?.logdata?.prenick
                  : ``,
                dayjs(d?.logdate)?.format("YYYY-MM-DD HH:mm"),
              ];
            }}
            dataUrl={
              `log?page=0&size=1000` +
              (filter?.type !== "" ? `&type=${filter?.type}` : ``) +
              (filter?.searchValue !== ""
                ? `&${filter?.searchType}=${filter?.searchValue}`
                : ``) +
              (filter?.endDate !== ""
                ? `&startdate=${filter?.startDate}&enddate=${filter?.endDate}`
                : ``)
            }
            title={filter?.typeLabel}
          />
        </ChatSection>
      )}

      {/* 매칭 로그 */}
      {filter?.type === "106" && (
        <MatchingLog type={filter?.type} filter={filter} onSavePage={() => setSavedPage(page)} />
      )}

      {loading && <Loader />}
    </>
  );
}

export const LineBoxContainer = styled.div`
  position: relative;
`;
